import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

// ============== Types ==============

export type UserRole = 'super_admin' | 'admin' | 'manager' | 'user';

export interface User {
  id: number;
  email: string;
  username: string;
  full_name: string | null;
  role: UserRole;
  is_active: boolean;
  is_verified: boolean;
  organization: string | null;
  department: string | null;
  position: string | null;
  profile_image_url: string | null;
  bio: string | null;
  subscription_tier: string;
  last_login: string | null;
  created_at: string;
}

interface AuthTokens {
  access_token: string;
  refresh_token: string;
}

interface RegisterData {
  email: string;
  username: string;
  password: string;
  full_name?: string;
  organization?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  loginWithGoogle: () => void;
  handleGoogleCallback: (code: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  hasRole: (requiredRole: UserRole) => boolean;
  hasMinimumRole: (minimumRole: UserRole) => boolean;
}

// ============== Constants ==============

// API URL 동적 감지: 현재 호스트 기반으로 자동 설정
const getApiBaseUrl = (): string => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const API_PORT = 8001;

  // 원격 도메인인 경우 (localhost가 아닌 경우)
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    const apiUrl = `${protocol}//${hostname}:${API_PORT}`;
    console.log('[AuthContext] ✅ Remote domain detected, API URL:', apiUrl);
    return apiUrl;
  }

  // 로컬 개발 환경
  console.log(`[AuthContext] Using local API URL: http://localhost:${API_PORT}`);
  return `http://localhost:${API_PORT}`;
};

// API_BASE_URL을 함수로 변경하여 매번 현재 location을 확인하도록 함
// 모듈 로드 시점이 아닌 실행 시점에 window.location을 확인
const getApiBaseUrlDynamic = (): string => {
  return getApiBaseUrl();
};

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'proposalcraft_access_token',
  REFRESH_TOKEN: 'proposalcraft_refresh_token',
  USER: 'proposalcraft_user',
};

const ROLE_HIERARCHY: Record<UserRole, number> = {
  user: 0,
  manager: 1,
  admin: 2,
  super_admin: 3,
};

// ============== Context ==============

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============== Provider ==============

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  // 초기 로드 시 저장된 인증 정보 확인
  useEffect(() => {
    const initAuth = async () => {
      try {
        const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        const savedUser = localStorage.getItem(STORAGE_KEYS.USER);

        if (accessToken && savedUser) {
          setUser(JSON.parse(savedUser));
          // 토큰 유효성 검증
          await refreshUser();
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        clearAuth();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // URL에서 OAuth 콜백 처리
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get('access_token');
    const refreshToken = urlParams.get('refresh_token');

    if (accessToken && refreshToken) {
      handleOAuthTokens(accessToken, refreshToken);
      // URL에서 토큰 파라미터 제거
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleOAuthTokens = async (accessToken: string, refreshToken: string) => {
    try {
      saveTokens({ access_token: accessToken, refresh_token: refreshToken });
      await refreshUser();
      // 성공적으로 로그인 완료
    } catch (error) {
      console.error('OAuth token handling failed:', error);
      clearAuth();
      // 에러 발생 시 로그인 페이지로 리다이렉트
      window.location.href = '/login?error=oauth_failed';
    }
  };

  const saveTokens = (tokens: AuthTokens) => {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
  };

  const clearAuth = () => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    setUser(null);
  };

  const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // 이메일/비밀번호 로그인
  const login = async (email: string, password: string) => {
    setIsLoading(true);
    // 매번 현재 location을 확인하여 API URL 결정
    const currentApiBaseUrl = getApiBaseUrlDynamic();
    const loginUrl = `${currentApiBaseUrl}/api/auth/login`;
    console.log('[AuthContext] Login attempt:', {
      url: loginUrl,
      apiBaseUrl: currentApiBaseUrl,
      hostname: window.location.hostname,
      protocol: window.location.protocol,
      port: window.location.port,
      fullLocation: window.location.href
    });
    
    try {
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      console.log('[AuthContext] Login response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url
      });

      if (!response.ok) {
        let errorMessage = '로그인에 실패했습니다.';
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
          console.error('[AuthContext] Login error response:', error);
        } catch (parseError) {
          console.error('[AuthContext] Failed to parse error response:', parseError);
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('[AuthContext] Login success:', { userId: data.user?.id, email: data.user?.email });
      
      // 응답 본문에 code: 403이 있는지 확인
      if (data && typeof data === 'object' && data.code === 403) {
        throw new Error('접근 권한이 없습니다.');
      }
      
      saveTokens(data);
      setUser(data.user);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
    } catch (error: any) {
      const msg = error.message || '로그인에 실패했습니다.';
      setError(msg);
      console.error('[AuthContext] Login failed:', msg);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // 이메일 회원가입
  const register = async (data: RegisterData) => {
    setIsLoading(true);
    const currentApiBaseUrl = getApiBaseUrlDynamic();
    try {
      const response = await fetch(`${currentApiBaseUrl}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const msg = response.status === 409 || errData.detail?.includes('already')
          ? '이미 등록된 이메일입니다.'
          : errData.detail || '회원가입에 실패했습니다.';
        setError(msg);
        throw new Error(msg);
      }

      const result = await response.json();
      
      // 응답 본문에 code: 403이 있는지 확인
      if (result && typeof result === 'object' && result.code === 403) {
        throw new Error('접근 권한이 없습니다.');
      }
      
      saveTokens(result);
      setUser(result.user);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(result.user));
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Google OAuth 로그인 시작
  const loginWithGoogle = () => {
    const currentApiBaseUrl = getApiBaseUrlDynamic();
    window.location.href = `${currentApiBaseUrl}/api/auth/google/login`;
  };

  // Google OAuth 콜백 처리
  const handleGoogleCallback = async (code: string) => {
    setIsLoading(true);
    const currentApiBaseUrl = getApiBaseUrlDynamic();
    try {
      const response = await fetch(`${currentApiBaseUrl}/api/auth/google/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Google 로그인에 실패했습니다.');
      }

      const data = await response.json();
      saveTokens(data);
      setUser(data.user);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
    } finally {
      setIsLoading(false);
    }
  };

  // 로그아웃
  const logout = async () => {
    const currentApiBaseUrl = getApiBaseUrlDynamic();
    try {
      await fetch(`${currentApiBaseUrl}/api/auth/logout`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      clearAuth();
    }
  };

  // 사용자 정보 새로고침
  const refreshUser = async () => {
    const currentApiBaseUrl = getApiBaseUrlDynamic();
    try {
      const response = await fetch(`${currentApiBaseUrl}/api/auth/me`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          // 토큰 만료 - 리프레시 시도
          const refreshed = await tryRefreshToken();
          if (!refreshed) {
            clearAuth();
            return;
          }
          // 리프레시 성공 후 다시 시도
          const retryResponse = await fetch(`${currentApiBaseUrl}/api/auth/me`, {
            headers: getAuthHeaders(),
          });
          if (retryResponse.ok) {
            const userData = await retryResponse.json();
            setUser(userData);
            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
          }
          return;
        }
        throw new Error('사용자 정보를 가져올 수 없습니다.');
      }

      const userData = await response.json();
      
      // 응답 본문에 code: 403이 있는지 확인
      if (userData && typeof userData === 'object' && userData.code === 403) {
        throw new Error('접근 권한이 없습니다.');
      }
      
      setUser(userData);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
    } catch (error) {
      console.error('Refresh user failed:', error);
      clearAuth();
    }
  };

  // 토큰 리프레시
  const tryRefreshToken = async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (!refreshToken) return false;

    const currentApiBaseUrl = getApiBaseUrlDynamic();
    try {
      const response = await fetch(`${currentApiBaseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) return false;

      const data = await response.json();
      saveTokens(data);
      setUser(data.user);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
      return true;
    } catch {
      return false;
    }
  };

  // 역할 확인
  const hasRole = (requiredRole: UserRole): boolean => {
    if (!user) return false;
    return user.role === requiredRole;
  };

  // 최소 역할 확인
  const hasMinimumRole = (minimumRole: UserRole): boolean => {
    if (!user) return false;
    return ROLE_HIERARCHY[user.role] >= ROLE_HIERARCHY[minimumRole];
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    clearError,
    login,
    register,
    loginWithGoogle,
    handleGoogleCallback,
    logout,
    refreshUser,
    hasRole,
    hasMinimumRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ============== Hook ==============

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ============== Protected Route Component ==============

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: UserRole;
  minimumRole?: UserRole;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  minimumRole,
}) => {
  const { isAuthenticated, isLoading, hasRole, hasMinimumRole } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-3">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        <p className="text-sm text-gray-500">로딩 중...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if ((requiredRole && !hasRole(requiredRole)) || (minimumRole && !hasMinimumRole(minimumRole))) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">접근 권한 없음</h1>
        <p className="text-gray-600 mb-4">이 페이지에 접근할 권한이 없습니다.</p>
        <a href="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
          대시보드로 돌아가기
        </a>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthContext;

